import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { PUBLIC_PROFILE, GET_USER_ADS } from '../graphql/queries';
import Layout from '../components/Layout';
import MobileLayout from '../components/Mobile/MobileLayout';
import { useIsMobile } from '../hooks/useIsMobile';
import { getAbsoluteImageUrl } from '../lib/imageUtils';
import {
  MapPinIcon,
  CalendarIcon,
  TagIcon,
  EyeIcon,
  StarIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { Button, Card, Row, Col, Badge } from 'react-bootstrap';

const PublicProfile: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState<'active' | 'all'>('active');

  const { data: profileData, loading: profileLoading, error: profileError } = useQuery(PUBLIC_PROFILE, {
    variables: { username },
    skip: !username,
  });

  const { data: adsData, loading: adsLoading } = useQuery(GET_USER_ADS, {
    variables: { username, limit: 20, offset: 0 },
    skip: !username,
  });

  const profile = profileData?.publicProfile;
  const userAds = adsData?.userAds || [];

  // Filter ads based on active tab
  const filteredAds = userAds.filter((ad: any) =>
    activeTab === 'active' ? ad.isActive : true
  );

  const activeCount = userAds.filter((ad: any) => ad.isActive).length;
  const allCount = userAds.length;

  // Handle error state
  if (profileError) {
    const content = (
      <div className="py-4 px-3">
        <Card className="border-danger bg-danger bg-opacity-10 p-4 text-center">
          <h2 className="fs-5 fw-bold text-danger mb-2">Error Loading Profile</h2>
          <p className="text-danger">{profileError.message}</p>
          <Link to="/" className="mt-3 d-inline-block text-primary text-decoration-none">
            Return to Home
          </Link>
        </Card>
      </div>
    );

    return isMobile ? <MobileLayout>{content}</MobileLayout> : <Layout>{content}</Layout>;
  }

  if (profileLoading) {
    const content = (
      <div className="py-4 px-3">
        <div className="text-center py-5">
          <div className="spinner-border text-primary mx-auto" role="status" style={{ width: '48px', height: '48px' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-secondary">Loading profile...</p>
        </div>
      </div>
    );
    return isMobile ? <MobileLayout>{content}</MobileLayout> : <Layout showSidebar={false}>{content}</Layout>;
  }

  if (!profile) {
    const content = (
      <div className="py-4 px-3">
        <Card className="shadow p-4 text-center">
          <h1 className="fs-4 fw-bold text-dark mb-3">Profile Not Found</h1>
          <p className="text-secondary mb-4">The profile you're looking for doesn't exist or has been removed.</p>
          <Link to="/" className="text-primary text-decoration-none">
            Return to Home
          </Link>
        </Card>
      </div>
    );
    return isMobile ? <MobileLayout>{content}</MobileLayout> : <Layout showSidebar={false} containerSize="lg">{content}</Layout>;
  }

  const content = (
    <>
      {/* Cover Image */}
      <div className="position-relative rounded-top-4 overflow-hidden" style={{ height: '192px', background: 'linear-gradient(to right, var(--bs-primary), #4f46e5)' }}>
        {profile.coverImage && (
          <img
            src={getAbsoluteImageUrl(profile.coverImage) || ''}
            alt="Cover"
            className="w-100 h-100 object-cover rounded-top-4"
          />
        )}
      </div>

      {/* Profile Header */}
      <Card className="rounded-bottom-4 shadow rounded-top-0 border-0">
        <Card.Body className="px-4 pb-4">
          {/* Avatar */}
          <div className="position-relative mb-n5" style={{ marginTop: '-64px' }}>
            <div className="rounded-circle border border-4 border-white bg-light overflow-hidden" style={{ width: '128px', height: '128px' }}>
              {profile.profilePicture ? (
                <img
                  src={getAbsoluteImageUrl(profile.profilePicture) || ''}
                  alt={profile.displayName}
                  className="w-100 h-100 object-cover"
                />
              ) : (
                <div className="w-100 h-100 d-flex align-items-center justify-content-center fs-1 fw-bold text-muted" style={{ background: 'linear-gradient(to bottom right, var(--bs-primary), #4f46e5)' }}>
                  {profile.displayName?.[0] || profile.username?.[0] || 'U'}
                </div>
              )}
            </div>
          </div>

          {/* Profile Info */}
          <div className="pt-5">
            <div className="d-flex justify-content-between align-items-start mb-4">
              <div>
                <div className="d-flex align-items-center gap-2">
                  <h1 className="display-6 fw-bold text-dark">
                    {profile.displayName || profile.username}
                  </h1>
                  {profile.isVerified && (
                    <CheckBadgeIcon className="text-primary" style={{ width: '28px', height: '28px' }} />
                  )}
                </div>
                <p className="text-secondary">@{profile.username}</p>
                {profile.userType && (
                  <Badge bg={profile.userType === 'company' ? 'primary' : 'success'} className="mt-2">
                    {profile.userType === 'company' ? 'Company' : 'Individual'}
                  </Badge>
                )}
              </div>
              {user && user.username !== username && (
                <Link
                  to={`/ad/message?user=${profile.id}`}
                  className="btn btn-primary"
                >
                  Contact Seller
                </Link>
              )}
            </div>

            {profile.bio && (
              <div className="mb-4">
                <h3 className="fs-5 fw-semibold text-dark mb-2">About</h3>
                <p className="text-secondary">{profile.bio}</p>
              </div>
            )}

            {/* Stats */}
            <Row className="g-3 mb-4">
              <Col xs={6} md={3}>
                <Card className="text-center bg-light border-0">
                  <Card.Body className="py-3">
                    <TagIcon className="text-primary mx-auto mb-2" style={{ width: '24px', height: '24px' }} />
                    <div className="fs-4 fw-bold text-dark">{profile.activeAds || 0}</div>
                    <div className="small text-secondary">Active Ads</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={6} md={3}>
                <Card className="text-center bg-light border-0">
                  <Card.Body className="py-3">
                    <TagIcon className="text-secondary mx-auto mb-2" style={{ width: '24px', height: '24px' }} />
                    <div className="fs-4 fw-bold text-dark">{profile.totalAds || 0}</div>
                    <div className="small text-secondary">Total Ads</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={6} md={3}>
                <Card className="text-center bg-light border-0">
                  <Card.Body className="py-3">
                    <StarIcon className="text-warning mx-auto mb-2" style={{ width: '24px', height: '24px' }} />
                    <div className="fs-4 fw-bold text-dark">
                      {profile.averageRating?.toFixed(1) || 'N/A'}
                    </div>
                    <div className="small text-secondary">Rating</div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={6} md={3}>
                <Card className="text-center bg-light border-0">
                  <Card.Body className="py-3">
                    <CalendarIcon className="text-success mx-auto mb-2" style={{ width: '24px', height: '24px' }} />
                    <div className="small fw-bold text-dark">
                      {profile.dateJoined
                        ? new Date(profile.dateJoined).toLocaleDateString()
                        : 'N/A'}
                    </div>
                    <div className="small text-secondary">Member Since</div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Location */}
            {(profile.city || profile.country) && (
              <div className="d-flex align-items-center text-secondary mb-3">
                <MapPinIcon className="me-2" style={{ width: '20px', height: '20px' }} />
                {[profile.city, profile.country].filter(Boolean).join(', ')}
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Ads Section */}
      <div className="mt-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <h2 className="fs-4 fw-bold text-dark">Listings by {profile.displayName || profile.username}</h2>

          {/* Tabs */}
          <div className="d-flex gap-2">
            <Button
              variant={activeTab === 'active' ? 'primary' : 'light'}
              onClick={() => setActiveTab('active')}
            >
              Active ({activeCount})
            </Button>
            <Button
              variant={activeTab === 'all' ? 'primary' : 'light'}
              onClick={() => setActiveTab('all')}
            >
              All ({allCount})
            </Button>
          </div>
        </div>

        {adsLoading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary mx-auto" role="status" style={{ width: '48px', height: '48px' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-secondary">Loading listings...</p>
          </div>
        ) : filteredAds.length === 0 ? (
          <Card className="shadow p-4 text-center">
            <Card.Body>
              <TagIcon className="text-muted mx-auto mb-3" style={{ width: '64px', height: '64px' }} />
              <h3 className="fs-5 fw-semibold text-dark mb-2">No listings yet</h3>
              <p className="text-secondary">
                {activeTab === 'active'
                  ? 'This user has no active listings at the moment.'
                  : 'This user has not posted any listings yet.'}
              </p>
            </Card.Body>
          </Card>
        ) : (
          <Row className="g-4">
            {filteredAds.map((ad: any) => (
              <Col md={6} lg={4} key={ad.id}>
                <Link
                  to={`/ad/${ad.slug}`}
                  className="text-decoration-none"
                >
                  <Card className="h-100 border hover-shadow transition-shadow overflow-hidden">
                    {/* Image */}
                    <div className="position-relative">
                      {ad.primaryImage ? (
                        <img
                          src={getAbsoluteImageUrl(ad.primaryImage) || '/placeholder-ad.png'}
                          alt={ad.title}
                          className="w-100 object-cover"
                          style={{ height: '192px' }}
                        />
                      ) : (
                        <div
                          className="w-100 d-flex align-items-center justify-content-center"
                          style={{ height: '192px', background: 'linear-gradient(to bottom right, #f3f4f6, #e5e7eb)' }}
                        >
                          <TagIcon className="text-muted" style={{ width: '64px', height: '64px' }} />
                        </div>
                      )}
                      {ad.isFeatured && (
                        <div className="position-absolute top-0 start-0 m-2 bg-primary text-white small px-2 py-1 rounded">
                          Featured
                        </div>
                      )}
                      <div className="position-absolute bottom-0 end-0 m-2 bg-dark bg-opacity-75 text-white small px-2 py-1 rounded d-flex align-items-center gap-1">
                        <EyeIcon style={{ width: '12px', height: '12px' }} />
                        {ad.viewCount || 0}
                      </div>
                    </div>

                    {/* Content */}
                    <Card.Body className="p-3">
                      <h3 className="fw-semibold text-dark mb-2 text-truncate" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{ad.title}</h3>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fs-5 fw-bold text-primary">
                          Rs. {Number(ad.price).toLocaleString()}
                        </span>
                        <span className="small text-secondary bg-light px-2 py-1 rounded">
                          {ad.condition}
                        </span>
                      </div>
                      <div className="d-flex align-items-center small text-secondary">
                        <MapPinIcon className="me-1" style={{ width: '16px', height: '16px' }} />
                        {ad.city || ad.location}
                      </div>
                      {ad.category && (
                        <div className="mt-2 small text-muted">
                          in {ad.category.name}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Link>
              </Col>
            ))}
          </Row>
        )}
      </div>
    </>
  );

  return isMobile ? <MobileLayout>{content}</MobileLayout> : <Layout showSidebar={false} containerSize="lg">{content}</Layout>;
};

export default PublicProfile;
